import UUIDv4 from 'uuid/v4'
import { ConsentResponse } from 'services/api'
import {
  Consents,
  EXPIRATION_INTERVAL_DAYS,
  FundsConfirmationSchemes,
  LocalStorageKeys,
  SCHEME_FORMATS,
} from 'constants/common'

import { currencies } from 'constants/currencies'
import { addDays, differenceInCalendarDays, format } from 'date-fns'

const FINGERPRINT = 'fingerprint'

export function getParameterByName(name: string) {
  const match = RegExp(`[?&]${name}=([^&]*)`).exec(window.location.search)
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '))
}

export function getFingerprint() {
  if (!window.localStorage.getItem(FINGERPRINT)) {
    window.localStorage.setItem(FINGERPRINT, UUIDv4())
  }
  return window.localStorage.getItem(FINGERPRINT)
}

export function getFromLocalStorage(key: string): string {
  return window.localStorage.getItem(key) || ''
}

export function setToLocalStorage(key: string, value?: string) {
  if (typeof value === 'undefined') {
    window.localStorage.removeItem(key)
  } else {
    window.localStorage.setItem(key, value)
  }
}

export function removeTokensFromStorage() {
  setToLocalStorage(LocalStorageKeys.ACCESS_TOKEN)
  setToLocalStorage(LocalStorageKeys.VERIFICATION_TOKEN)
}

export function noop() {}

export function formatAccountInfo(details: ConsentResponse[Consents.FUNDS_CONFIRMATION]) {
  switch (details.scheme) {
    case FundsConfirmationSchemes.SORT_AND_ACCOUNT:
      return `
        ${details.identification.slice(0, 6).replace(/(\d{2})(\d{2})(\d+)/, '$1-$2-$3')}
        ${details.identification.slice(6)}
      `
    default:
      return details.identification
  }
}

const repeat = (char: string, times: number): string => Array(times + 1).join(char)

const formatMoneyInteger = (integer: string) => {
  const numsCount = integer.length
  const threes = Math.floor(numsCount / 3)

  if (!threes) {
    return integer
  }

  const remaining = numsCount % 3
  const patternPartRemain = remaining ? `(\\d{${remaining}})` : ''
  const pattern = new RegExp(`${patternPartRemain}${repeat('(\\d{3})', threes)}`)
  const matched = integer.match(pattern)
  return matched ? matched.slice(1).join(',') : integer
}

export type FormatMoneyRequest = {
  currency: string
  amount: number
  showDecimal?: boolean
}

export const formatMoney = ({
  currency,
  amount,
  showDecimal = false,
}: FormatMoneyRequest) => {
  const { symbol = '', fraction = 2 } = currencies[currency] || {}
  if (!amount) {
    return `${symbol}0`
  }

  const sign = amount > 0 ? '' : '-'
  const realAmount = Math.abs(amount / Math.pow(10, fraction))
  const [integer, remain] = realAmount.toString().split('.')
  const whole = formatMoneyInteger(integer)
  let decimal = ''
  if (remain && remain.length) {
    decimal = `${remain}${repeat('0', fraction - remain.length)}`
  } else if (showDecimal) {
    decimal = repeat('0', fraction)
  }

  return `${sign}${symbol}${whole}${decimal ? `.${decimal}` : ''}`
}

export const formatByScheme = ({
  scheme,
  identification,
}: {
  scheme: string
  identification: string
}) => (SCHEME_FORMATS[scheme] ? SCHEME_FORMATS[scheme](identification) : identification)

export const calculateConsentExpirationDate = (expirationDate?: string) => {
  const expirationTimestamp = expirationDate
    ? new Date(expirationDate).getTime()
    : Infinity
  const maxExpirationTimestamp = addDays(Date.now(), EXPIRATION_INTERVAL_DAYS).getTime()

  const expiresAt = new Date(Math.min(expirationTimestamp, maxExpirationTimestamp))
  const expiresAfter = differenceInCalendarDays(expiresAt, Date.now())
  const expiresAtString = format(expiresAt, 'd MMM yyyy')

  return { expiresAfter, expiresAtString }
}
